const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Build the initial form state object from a schema definition and an optional
 * existing row. Switch fields are coerced to boolean; all others to string so
 * controlled MUI inputs never receive undefined/null.
 *
 * @param {Array<{key: string, type: string, default?: any}>} schema
 * @param {object} [row] - Existing DB row for edit mode; omit for create.
 * @returns {object} Initial form values keyed by schema field keys.
 */
export function buildInitialFromSchema(schema, row = {}) {
  const out = {};
  for (const f of schema) {
    if (f.type === "switch") {
      out[f.key] = row[f.key] != null ? !!row[f.key] : (f.default ?? false);
    } else {
      out[f.key] = row[f.key] != null ? String(row[f.key]) : (f.default != null ? String(f.default) : "");
    }
  }
  return out;
}

/**
 * Validate a form object against a schema. Fields whose `visibleWhen(form)`
 * returns false are skipped entirely. Switch fields are always valid.
 * Number fields validate isNaN and call `field.validate(v)` if provided.
 *
 * `f.label` is expected to already be resolved display text (the schema
 * arrays that build it call the field's own translation function of `t`
 * before this runs — see e.g. VehiclesTab.jsx's `schema` useMemo), so the
 * generated messages below only need `t` for their own template text.
 *
 * @param {Array<object>} schema
 * @param {object} form
 * @param {(key: string, vars?: object) => string} t
 * @returns {object} Error map keyed by field key; empty object = valid.
 */
export function validateSchema(schema, form, t) {
  const errs = {};
  for (const f of schema) {
    if (f.visibleWhen && !f.visibleWhen(form)) continue;
    if (f.type === "switch") continue; // booleans are always valid
    const v = (form[f.key] ?? "").toString().trim();
    if (f.required && !v) { errs[f.key] = t("provider.entitySchema.requiredError", { label: f.label }); continue; }
    if (v) {
      if (f.type === "number" && Number.isNaN(Number(v))) errs[f.key] = t("provider.entitySchema.invalidError", { label: f.label });
      else if (f.type === "email" && !EMAIL_RE.test(v)) errs[f.key] = t("provider.entitySchema.invalidEmail");
      else if (f.validate) { const m = f.validate(v); if (m) errs[f.key] = m; }
    }
  }
  return errs;
}
